import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import { Box, ButtonDropdown, ButtonDropdownProps, Popover } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { RecentPortfoliosActions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { CookieConsentContext } from '../../contexts/CookieConsentContext';
import { RecentPortfoliosContext } from '../../contexts/RecentPortfoliosContext';
import { getDropDownContent, noRecentPortfolios, viewAllPortfolios } from './recent-portfolios-helper';

const NUM_RECENT_PORTFOLIOS = 5;

type Props = AuthContextInterface & {
    showWhileLoading: boolean;
}

const RecentPortfoliosList = ({ auth, showWhileLoading }: Props) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    const isAuthenticated = auth.isAuthenticated();

    const isMounted = useIsMounted();

    const [recentPortfoliosList, setRecentPortfoliosList] = useState<ButtonDropdownProps.ItemOrGroup[]>([noRecentPortfolios, viewAllPortfolios]);
    const [loading, setLoading] = useState(false);
    const [ hasCookieConsent, setHasCookieConsent ] = useState(false);
    const { cookie } = useContext(CookieConsentContext);
    const { recentPortfolioIds, getMostRecent, visited } = useContext(RecentPortfoliosContext);

    const history = useHistory();

    useEffect(() => {
        setHasCookieConsent(cookie.functional);
    }, [ cookie ]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        if (portfolioId) {
            visited(portfolioId, RecentPortfoliosActions.ADD);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isAuthenticated, portfolioId ]);

    useEffect(() => {
        const loadPortfolios = async () => {
            setLoading(true);
            try {
                const portfolioIds = getMostRecent(NUM_RECENT_PORTFOLIOS);
                const portfolios = await getDropDownContent(portfolioIds);
                if (isMounted()) {
                    setRecentPortfoliosList(portfolios);
                }
            } catch (err) {
                console.log(err);
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        };
        
        loadPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ recentPortfolioIds ]);

    const dropdownText = portfolioId && !loading ? recentPortfoliosList[0].text : constants.RECENT_PORTFOLIOS;

    if (auth.isAuthenticated() && (showWhileLoading || !loading)) {
        if (hasCookieConsent) {
            return (
                <ButtonDropdown 
                    items={recentPortfoliosList}
                    loading={loading}
                    onItemClick={event => {
                        if(event.detail.href) {
                            event.preventDefault();
                            history.push(event.detail.href);
                        }
                    }}
                >
                    {dropdownText}
                </ButtonDropdown>
            );
        } else if (showWhileLoading) {
            return (
                <Popover
                    content={
                        <Box>{constants.RECENT_PORTFOLIOS_COOKIE_NOT_SET_MSG}</Box>
                    }
                >
                    {<Box>{dropdownText}</Box>}
                </Popover>
            );
        } else {
            return null;
        }
    } else {
        return null;
    }
};

export default withAuthContext(RecentPortfoliosList);
